import { getBaseHref } from '@casops/bootstrap';
import { SerializedError } from '@reduxjs/toolkit';
import {
  BaseQueryError,
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';

export enum ModelName {
  Asset = 'Asset',
  AssetDetail = 'AssetDetail',
  AssetConfiguration = 'AssetConfiguration',
  AssetSelfTestActiveFaults = 'AssetSelfTestActiveFaults',
  AssetDeviceFile = 'AssetDeviceFile',
  AssetGroup = 'AssetGroup',
  AssetTrack = 'AssetTrack',
  Certificate = 'Certificate',
  ConfigurationGroup = 'ConfigurationGroup',
  ConfigurationGroupPackage = 'ConfigurationGroupPackage',
  ConfigurationGroupAsset = 'ConfigurationGroupAsset',
  Geofence = 'Geofence',
  GeofenceArea = 'GeofenceArea',
  GeofenceHoldTime = 'GeofenceHoldTime',
  GeofencePinDrop = 'GeofencePinDrop',
  GeofenceSpeedLimit = 'GeofenceSpeedLimit',
  GeofenceStyle = 'GeofenceStyle',
  LibraryItem = 'LibraryItem',
  PendingApproval = 'PendingApproval',
  User = 'User',
  Diagnostic = 'Diagnostic',
  DeviceMessage = 'DeviceMessage',
  SafetyAlertDuress = 'SafetyAlertDuress',
  SafetyAlertManDown = 'SafetyAlertManDown',
  SafetyManager = 'SafetyManager',
}

export enum ModelAction {
  Acknowledge = 'Acknowledge',
  // TODO: Most model mutations could be added here to utilise logic and translation patterns (e.g. Unlock, Delete, Assign, etc.)
}

export interface CollectionModel<T> {
  items: T[];
}

export interface CollectionFilteredModel<T> extends CollectionModel<T> {
  countAll: number;
  countFilter: number;
}

// initialize an empty api service that we'll inject endpoints into later as needed

const baseQuery = fetchBaseQuery({
  baseUrl: getBaseHref(),
  prepareHeaders: (headers, { getState }) => {
    headers.set('X-CSRF', '1');
    return headers;
  },
});

const permittedAnonymousApis = ['api/settings/ui'];

export type ApiBaseQueryFn = BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError | SerializedError | undefined | unknown
>;

export type ApiBaseQueryError = BaseQueryError<ApiBaseQueryFn>;

const baseQueryWithAutoRedirect: ApiBaseQueryFn = async (args, api, extraOptions) => {
  const result = await baseQuery(args, api, extraOptions);
  if (result.error && result.error.status === 401) {
    let url = '';

    if (typeof args === 'string') {
      url = args;
    } else {
      const fetchArgs = args as FetchArgs;
      url = fetchArgs?.url ?? '';
    }

    if (!permittedAnonymousApis.includes(url)) {
      // Cookie has expired - redirect to login
      console.debug('auth cookie expired');
      window.location.href = getBaseHref();
    }
  }
  return result;
};

export enum QueryTag {
  AssetApproval = 'AssetApproval',
  AssetRegistered = 'AssetRegistered',
  AssetGroup = 'AssetGroup',
  AssetTrack = 'AssetTrack',
  Certificate = 'Certificate',
  ConfigurationGroup = 'ConfigurationGroup',
  DeviceMessage = 'DeviceMessage',
  Geofence = 'Geofence',
  GeofenceVersion = 'GeofenceVersion',
  GeofenceArea = 'GeofenceArea',
  GeofenceHoldTime = 'GeofenceHoldTime',
  GeofenceSpeedLimit = 'GeofenceSpeedLimit',
  GeofenceStyle = 'GeofenceStyle',
  GeofenceGeoJson = 'GeofenceGeoJson',
  GeofencePinDrop = 'GeofencePinDrop',
  LibraryItem = 'LibraryItem',
  SafetyAlert = 'SafetyAlert',
  SettingsRole = 'SettingsRole',
  SettingsUi = 'SettingsUi',
  SettingsPersonnelSafety = 'SettingsPersonnelSafety',
  SettingsLibraryItemType = 'SettingsLibraryItemType',
  SettingsLocale = 'SettingsLocale',
  User = 'User',
  UserAuth = 'UserAuth',
}

export const TagList = 'ALL';

const tagTypes: QueryTag[] = [...Object.values(QueryTag)];

export const casOpsApi = createApi({
  baseQuery: baseQueryWithAutoRedirect,
  tagTypes,
  endpoints: () => ({}),
});
